import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Storage as StorageIcon,
  Email as EmailIcon,
  Folder as FolderIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Link as LinkIcon,
  Cloud as CloudIcon,
  Storage as DatabaseIcon,
  Api as ApiIcon,
  Camera as CameraIcon,
  Traffic as SignalIcon,
  Speed as SpeedIcon,
  Message as MessageIcon
} from '@mui/icons-material';

const AgentContext = ({ agent, entities, externalSources, sharedFolders, emails, onRefreshData }) => {
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [showEntityDialog, setShowEntityDialog] = useState(false);
  const [showSourceDialog, setShowSourceDialog] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'error';
      case 'Maintenance': return 'warning';
      case 'Connected': return 'success';
      case 'Disconnected': return 'error';
      default: return 'default';
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'Camera': return <CameraIcon />;
      case 'Signal': return <SignalIcon />;
      case 'SpeedDetector': return <SpeedIcon />;
      case 'MessageBoard': return <MessageIcon />;
      default: return <StorageIcon />;
    }
  };

  const getSourceIcon = (type) => {
    switch (type) {
      case 'API': return <ApiIcon />;
      case 'Database': return <DatabaseIcon />;
      default: return <CloudIcon />;
    }
  };

  const handleEntityClick = (entity) => {
    setSelectedEntity(entity);
    setShowEntityDialog(true);
  };

  const handleSourceClick = (source) => {
    setSelectedSource(source);
    setShowSourceDialog(true);
  };

  const handleCloseEntityDialog = () => {
    setShowEntityDialog(false);
    setSelectedEntity(null);
  };

  const handleCloseSourceDialog = () => {
    setShowSourceDialog(false);
    setSelectedSource(null);
  };

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Context Access
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRefreshData}
          >
            Refresh
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Entities Section */}
          <Grid item xs={12} md={6}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorageIcon />
                  <Typography variant="h6">
                    Available Entities ({entities.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {entities.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <StorageIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      No entities available
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {entities.slice(0, 10).map((entity) => (
                      <ListItem 
                        key={entity._id} 
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          border: 1, 
                          borderColor: 'divider', 
                          borderRadius: 1, 
                          mb: 1 
                        }}
                        onClick={() => handleEntityClick(entity)}
                      >
                        <ListItemIcon>
                          {getEntityIcon(entity.entityType?.name)}
                        </ListItemIcon>
                        <ListItemText
                          primary={entity.name}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {entity.entityType?.name} • {entity.location?.address || 'No location'}
                              </Typography>
                              <Box sx={{ mt: 0.5 }}>
                                <Chip 
                                  label={entity.status} 
                                  size="small" 
                                  color={getStatusColor(entity.status)}
                                />
                              </Box>
                            </Box>
                          }
                        />
                        <IconButton size="small">
                          <VisibilityIcon />
                        </IconButton>
                      </ListItem>
                    ))}
                    {entities.length > 10 && (
                      <ListItem>
                        <ListItemText
                          primary={`+${entities.length - 10} more entities`}
                          sx={{ textAlign: 'center', fontStyle: 'italic' }}
                        />
                      </ListItem>
                    )}
                  </List>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* External Sources Section */}
          <Grid item xs={12} md={6}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudIcon />
                  <Typography variant="h6">
                    External Data Sources ({externalSources.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {externalSources.map((source) => (
                    <ListItem 
                      key={source.id} 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        border: 1, 
                        borderColor: 'divider', 
                        borderRadius: 1, 
                        mb: 1 
                      }}
                      onClick={() => handleSourceClick(source)}
                    >
                      <ListItemIcon>
                        {getSourceIcon(source.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={source.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {source.type} • {source.endpoint}
                            </Typography>
                            <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Chip 
                                label={source.status} 
                                size="small" 
                                color={getStatusColor(source.status)}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Last sync: {source.lastSync}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                      <IconButton size="small">
                        <RefreshIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Shared Folders Section */}
          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderIcon />
                  <Typography variant="h6">
                    Shared Folders ({sharedFolders.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {sharedFolders.map((folder) => (
                    <Grid item xs={12} sm={6} key={folder.id}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { boxShadow: 4 }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <FolderIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6" noWrap>{folder.name}</Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {folder.files} files • {folder.size}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Modified: {folder.lastModified}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Email Access Section */}
          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon />
                  <Typography variant="h6">
                    Email Access ({emails.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {emails.map((email) => (
                    <ListItem key={email.id} sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <ListItemIcon>
                        <EmailIcon color={email.unread ? 'primary' : 'disabled'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                              variant="body1" 
                              fontWeight={email.unread ? 'bold' : 'normal'}
                              sx={{ flex: 1 }}
                            >
                              {email.subject}
                            </Typography>
                            {email.unread && <Chip label="New" size="small" color="primary" />}
                          </Box>
                        }
                        secondary={`From: ${email.from} • ${email.date}`}
                      />
                      <Chip 
                        label={email.priority} 
                        size="small" 
                        color={email.priority === 'high' ? 'error' : email.priority === 'medium' ? 'warning' : 'success'}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Paper>

      {/* Entity Details Dialog */}
      <Dialog 
        open={showEntityDialog} 
        onClose={handleCloseEntityDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Entity Details: {selectedEntity?.name}
        </DialogTitle>
        <DialogContent>
          {selectedEntity && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Basic Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Name" 
                        secondary={selectedEntity.name} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Type" 
                        secondary={selectedEntity.entityType?.name} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Status" 
                        secondary={
                          <Chip 
                            label={selectedEntity.status} 
                            color={getStatusColor(selectedEntity.status)}
                            size="small"
                          />
                        } 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Location" 
                        secondary={selectedEntity.location?.address || 'No location'} 
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Actions</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button variant="outlined" startIcon={<VisibilityIcon />}>
                      View Details
                    </Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />}>
                      Export Data
                    </Button>
                    <Button variant="outlined" startIcon={<UploadIcon />}>
                      Update Configuration
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEntityDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Source Details Dialog */}
      <Dialog 
        open={showSourceDialog} 
        onClose={handleCloseSourceDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Data Source: {selectedSource?.name}
        </DialogTitle>
        <DialogContent>
          {selectedSource && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Connection Details</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Name" 
                        secondary={selectedSource.name} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Type" 
                        secondary={selectedSource.type} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Status" 
                        secondary={
                          <Chip 
                            label={selectedSource.status} 
                            color={getStatusColor(selectedSource.status)}
                            size="small"
                          />
                        } 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Endpoint" 
                        secondary={selectedSource.endpoint} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Last Sync" 
                        secondary={selectedSource.lastSync} 
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Actions</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button variant="outlined" startIcon={<RefreshIcon />}>
                      Test Connection
                    </Button>
                    <Button variant="outlined" startIcon={<SettingsIcon />}>
                      Configure
                    </Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />}>
                      Sync Now
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSourceDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgentContext; 